<template>
  <div class="event-vertical-card">
    <div>
      <v-card :color="disciplineColor" class="event-vertical-card--discipline">
        <div class="d-flex flex-row">
          <div style="text-transform: capitalize">
            {{ disciplineDisplayName
            }}<AppShowEventReferenceCode :event="event" force-current-color
              ><template #prepend> | </template></AppShowEventReferenceCode
            >
          </div>
          <div>
            <AppShowEventStatus :event="event" no-button block always-show />
          </div>
        </div>
      </v-card>
    </div>
    <EventCardAlert v-if="showAlert" :text="event.alertText" />

    <v-card
      class="py-4 px-3 event-vertical-card--content"
      :to="ignoreLink ? null : linkToEvent"
      :target="embeddedMode ? '_blank' : undefined"
    >
      <div class="px-3" style="position: relative">
        {{ eventDate }}
        <br />
        <AppShowCountry :country="event.country">
          <template #append
            >,<span
              :class="event.venueAddress && mapModal ? 'place-modal-link' : ''"
              @click.prevent="
                event.venueAddress && mapModal
                  ? $emit('placeDetails', event)
                  : null
              "
            >
              {{ regionName }},
              {{ event.place }}
            </span>
          </template>
        </AppShowCountry>

        <v-icon
          v-if="event.isHighlighted"
          class="star-icon"
          color="star"
          style="position: absolute; top: 0px; right: 20px; font-size: 24px"
          >$iconStar</v-icon
        >
      </div>

      <div>
        <v-row desne align="center">
          <v-col cols="5">
            <AppOrganLogo small :organiser="event.organiser" class="pa-3" />
          </v-col>

          <v-col cols="7">
            <div class="body-2">
              <b>
                <AppShowEventName :event="event" disable-link />
              </b>
            </div>
            <div class="muted--text">
              <AppOrganisationNameLink :organisation="event.organiser" />
              <template v-if="event.hostOrganisation">
                |
                <AppOrganisationNameLink
                  :organisation="event.hostOrganisation"
                />
              </template>
            </div>
          </v-col>
        </v-row>
      </div>

      <div
        :class="[
          showFullDescription
            ? ''
            : 'event-vertical-card--content--description',
          'my-2 px-3'
        ]"
      >
        <div
          v-html="
            showFullDescription
              ? event.description
              : truncate(event.description, {
                  length: 140,
                  seperator: ' '
                })
          "
        />
      </div>

      <div class="text-center secondary--text px-3">
        <template v-if="!event.noncompetition">
          <div>
            <AppShowCompetitionLevelNames
              :value="event.competitionLevels"
              class="mb-2"
            />

            <AppShowDifficultyLevels
              v-if="event.competitions"
              range
              class="mb-2"
              :value="event.competitions.map(c => c.difficultyLevel)"
            />

            <AppShowEventcodes :value="event.competitions" class="mb-2" />
          </div>
          <AppShowEventCupComponent
            v-if="event.cups && event.cups.length > 0"
            :event="event"
          />
        </template>
        <div v-else>
          <AppShowEventCategories :event-categories="event.eventCategories" />
          <AppShowProfQualificationsList
            :prof-qualifications="event.profQualifications"
            cut-off-list
          />
        </div>
      </div>

      <div>
        <div class="event-vertical-card--button">
          <AppShowRegistrationBtn
            v-if="eventLink == 'registration'"
            :event="event"
          />
          <AppShowResultsBtn
            v-else-if="eventLink == 'results'"
            :to="{ name: 'EventResultsPage', params: { eventId: event.id } }"
            margin-class="mr-2"
          />
        </div>
        <br />
        <AppShowRegistrationDeadline
          :event="event"
          show-applicable-check-schedule
          align-text="center"
          :target="embeddedMode ? '_blank' : undefined"
        />
      </div>
    </v-card>
  </div>
</template>
<script>
import { inject, computed } from "vue";
import { useTranslation } from "@/plugins/i18next/i18next";
import gql from "fraql";
import { truncate } from "lodash-es";
import { useEventCardHelpers } from "./EventCardMixin";

import AppShowCountry from "@AppShows/AppShowCountry.vue";
import AppShowEventCupComponent from "@/components/elements/AppShows/event/AppShowEventCupComponent.vue";

import AppOrganLogo from "@/components/elements/AppOrganLogo.vue";
import AppShowDifficultyLevels from "@/components/elements/AppShows/AppShowDifficultyLevels.vue";
import AppShowCompetitionLevelNames from "@/components/elements/AppShows/event/AppShowCompetitionLevelNames.vue";
import AppOrganisationNameLink from "@/components/elements/AppNameLinks/AppOrganisationNameLink.vue";
import AppShowRegistrationBtn from "@/components/elements/AppShows/event/AppShowRegistrationBtn.vue";
import AppShowEventcodes from "@/components/elements/AppShows/event/AppShowEventcodes.vue";
import AppShowEventStatus from "@/components/elements/AppShows/event/AppShowEventStatus.vue";
import AppShowEventName from "@AppShows/event/AppShowEventName.vue";
import AppShowResultsBtn from "@AppShows/event/AppShowResultsBtn.vue";
import AppShowRegistrationDeadline from "@AppShows/event/AppShowRegistrationDeadline.vue";
import AppShowEventCategories from "@AppShows/event/AppShowEventCategories.vue";
import AppShowProfQualificationsList from "@AppShows/AppShowProfQualificationsList.vue";

import EventCardAlert from "./EventCardAlert.vue";
import AppShowEventReferenceCode from "@AppShows/event/AppShowEventReferenceCode.vue";

export default {
  name: "EventCardVertical",
  components: {
    AppOrganLogo,
    AppShowDifficultyLevels,
    AppShowCompetitionLevelNames,
    AppShowEventCupComponent,
    AppShowRegistrationBtn,
    AppShowEventcodes,
    AppShowEventStatus,
    AppOrganisationNameLink,
    AppShowCountry,
    AppShowEventName,
    AppShowRegistrationDeadline,
    AppShowResultsBtn,
    AppShowEventCategories,
    EventCardAlert,
    AppShowProfQualificationsList,
    AppShowEventReferenceCode
  },
  inject: {
    isNoncompetition: {
      default: false
    }
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    mapModal: {
      type: Boolean,
      default: false
    },
    ignoreStatus: {
      type: Boolean,
      default: false
    },
    ignoreLink: {
      type: Boolean,
      default: false
    },
    showFullDescription: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const embeddedMode = inject("embeddedMode", false);
    const { t } = useTranslation();
    const {
      profilePage,
      regionName,
      eventDate,
      eventLink,
      showAlert,

      linkToEvent
    } = useEventCardHelpers(props);

    const disciplineDisplayName = computed(() => {
      if (props.event.discipline) {
        return t(
          "common:discipline." + props.event.discipline.name ||
            props.event.discipline.value,
          props.event.discipline.name
        );
      }
      return null;
    });

    const disciplineColor = computed(() => {
      if (props.event.discipline) {
        return (
          props.event.discipline.value ||
          props.event.discipline.name ||
          "showjumping"
        );
      }
      return "showjumping";
    });

    return {
      embeddedMode,
      profilePage,
      regionName,
      eventDate,
      eventLink,
      showAlert,
      linkToEvent,

      truncate,

      disciplineColor,
      disciplineDisplayName
    };
  },
  fragments: {
    event: gql`
      fragment _ on Event {
        id
        name
        noncompetition
        ${AppShowEventReferenceCode.fragments.eventFields}
        ${AppShowEventStatus.fragments.eventStatus}
        ${AppShowRegistrationBtn.fragments.event}
        ${AppShowRegistrationDeadline.fragments.eventFields}
        startDate
        endDate
        registrationEndDate
        description
        alertText
        isHighlighted
        disciplineId
        discipline @client {
          id
          name
        }
        eventCategoryIds
        eventCategories @client {
          ${AppShowEventCategories.fragments.eventCategoryFields}
        }
        profQualificationIds
        profQualifications @client {
          ${AppShowProfQualificationsList.fragments.profQualificationFields}
        }
        countryId
        organiser {
          id
          logoPath
          name
        }
        hostOrganisation {
          id
          name
        }
        regionId
        region @client {
          id
          name
        }
        place
        ${AppShowCompetitionLevelNames.fragments.competitionLevels}
        ${AppShowEventCupComponent.fragments.cups}
        ${AppShowDifficultyLevels.fragments.event}
        competitions {
          id
          ${AppShowRegistrationDeadline.fragments.competitionFields}
        }
      }`
  }
};
</script>
